import React, { useEffect, useState, useRef } from "react"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Row,
    Label,
} from "reactstrap"
import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"
import { validateBeforeConfirm } from "helpers/validation_helper"
import {
    getOfferInclusions,
    manageOfferInclusions,
    clearOfferInclusions,
    addInclusionToForm,
    updateInclusionInForm,
    removeInclusionFromForm,
    clearInclusionForm,
} from "store/offers/actions"
import ConfirmButton from "components/Common/ConfirmButton"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"
import AccessControlWrapper from "components/Common/PermissionButton"
import * as url from "../../helpers/url_helper"
import TextInput from "components/Common/TextInput"

export default function OfferInclusionsForm({
    page,
    match,
    history,
    offerType,
    canEdit,
    onTabCompletion,
    activeTab,
    tabId,
    createdOfferID,
    copyFromID,
    fromType,
}) {
    const [showAddForm, setShowAddForm] = useState(page === "add")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [editingInclusionIndex, setEditingInclusionIndex] = useState(null)
    const [isEditMode, setIsEditMode] = useState(false)
    const [currentInclusion, setCurrentInclusion] = useState({
        inclusionText: "",
    })

    const prevInclusionCreationSuccess = useRef(null)
    const prevOfferInclusionsError = useRef(null)
    const hasFetchedCopyData = useRef(false) // Track if we've fetched data from copied offer

    const dispatch = useDispatch()
    const detailID = match?.params?.id && match?.params?.id !== "add" ? match.params.id : null

    const offerID = page === "add" ? createdOfferID : detailID


    // For copy functionality, use the source offer ID to load inclusion data initially,
    // but switch to createdOfferID once the offer is created
    const sourceOfferID = (copyFromID && page === "add" && !createdOfferID) ? copyFromID : offerID

    // Validation schema
    const validationSchema = Yup.object({
        inclusionText: Yup.string()
            .required("Inclusion text is required")
            .max(500, "Inclusion text cannot exceed 500 characters")
            .trim("Inclusion text cannot contain leading or trailing spaces"),
    })

    // Form validation using Formik
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            inclusionText: currentInclusion.inclusionText,
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleAddInclusionToForm()
            }
        },
    })

    // Update form values when currentInclusion changes
    useEffect(() => {
        validation.setValues({
            inclusionText: currentInclusion.inclusionText,
        })
    }, [currentInclusion])

    const shouldDisplayError = error => {
        if (!error) return false

        const errorMessage = error.message || error.description || ""
        const errorCode = error.code || error.status || ""

        const noDataPatterns = ["no content available"]

        return !noDataPatterns.some(
            pattern =>
                errorMessage.toLowerCase().includes(pattern) ||
                errorCode.toString().includes("404")
        )
    }

    const {
        offerInclusions,
        offerInclusionsError,
        isLoadingOfferInclusions,
        inclusionFormData,
        inclusionCreationSuccess,
    } = useSelector(state => ({
        offerInclusions: state.offers.offerInclusions || [],
        offerInclusionsError: state.offers.offerInclusionsError,
        isLoadingOfferInclusions: state.offers.isLoadingOfferInclusions || false,
        inclusionFormData: state.offers.inclusionFormData || [],
        inclusionCreationSuccess: state.offers.inclusionCreationSuccess,
    }))

    // Pre-fetch offer inclusions when copying (even if tab is not active yet)
    useEffect(() => {
        // When copying, fetch data from source offer immediately
        if (copyFromID && page === "add" && !createdOfferID && !hasFetchedCopyData.current) {
            hasFetchedCopyData.current = true
            dispatch(
                getOfferInclusions({
                    headers: {
                        "input-parameters": { offerID: copyFromID },
                    },
                })
            )
        }
    }, [dispatch, copyFromID, page, createdOfferID])

    // Fetch offer inclusions when tab is active (for detail mode or after save)
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (sourceOfferID && isTabActive && page === "detail") {
            dispatch(
                getOfferInclusions({
                    headers: {
                        "input-parameters": { offerID: sourceOfferID },
                    },
                })
            )
        }
    }, [dispatch, sourceOfferID, activeTab, tabId, page])

    useEffect(() => {
        dispatch(clearInclusionForm())
        // Clear offer inclusions data when in add mode without copy to prevent showing stale data
        if (page === "add" && !copyFromID) {
            dispatch(clearOfferInclusions())
        }
    }, [dispatch, page, copyFromID])

    useEffect(() => {
        return () => {
            dispatch(clearOfferInclusions())
        }
    }, [dispatch])

    // Populate form data from fetched inclusions when copying
    useEffect(() => {
        if (copyFromID && page === "add" && offerInclusions.length > 0 && inclusionFormData.length === 0) {
            offerInclusions.forEach(inclusion => {
                const inclusionText = typeof inclusion === 'string' ? inclusion : inclusion.inclusionText
                if (inclusionText) {
                    dispatch(addInclusionToForm({ inclusionText }))
                }
            })
        }
    }, [copyFromID, page, offerInclusions, inclusionFormData.length, dispatch])

    const handleAddInclusionToForm = async () => {
        // Validate form before proceeding
        const isValid = await validateBeforeConfirm(validation)
        if (!isValid) {
            return
        }

        const newInclusion = {
            inclusionText: validation.values.inclusionText.trim(),
        }

        if (editingInclusionIndex !== null) {
            dispatch(updateInclusionInForm(editingInclusionIndex, newInclusion))
            setEditingInclusionIndex(null)
        } else {
            dispatch(addInclusionToForm(newInclusion))
        }

        // Reset form
        setCurrentInclusion({ inclusionText: "" })
        validation.resetForm()
    }

    const handleEditInclusion = (inclusion, index) => {
        setCurrentInclusion({
            inclusionText: inclusion.inclusionText,
        })
        setEditingInclusionIndex(index)
    }

    const handleDeleteInclusionFromForm = (index) => {
        dispatch(removeInclusionFromForm(index))

        if (editingInclusionIndex === index) {
            setEditingInclusionIndex(null)
            setCurrentInclusion({ inclusionText: "" })
            validation.resetForm()
        }
    }

    const handleCancelEdit = () => {
        setEditingInclusionIndex(null)
        setCurrentInclusion({ inclusionText: "" })
        validation.resetForm()
    }

    const handleFinalSubmit = () => {
        if (isSubmitting) return // Prevent multiple submissions

        setIsSubmitting(true)

        const payload = {
            offerID: parseInt(offerID),
            offerInclusions: inclusionFormData.map(inclusion => inclusion.inclusionText),
        }

        dispatch(manageOfferInclusions(payload, history))
    }

    // Handle success states
    useEffect(() => {
        if (inclusionCreationSuccess && inclusionCreationSuccess !== prevInclusionCreationSuccess.current) {
            setIsSubmitting(false)
            setIsEditMode(false)
            hasFetchedCopyData.current = false // Data saved, show saved listing now

            if (page !== "add") {
                setShowAddForm(false)
            }

            if (page === "add" && onTabCompletion) {
                onTabCompletion("3")
            }

            if (offerID) {
                dispatch(
                    getOfferInclusions({
                        headers: {
                            "input-parameters": { offerID: offerID },
                        },
                    })
                )
            }

            prevInclusionCreationSuccess.current = inclusionCreationSuccess
        }
    }, [inclusionCreationSuccess, page, offerID, dispatch, onTabCompletion])

    // Separate effect for error handling
    useEffect(() => {
        if (offerInclusionsError && offerInclusionsError !== prevOfferInclusionsError.current) {
            setIsSubmitting(false)
            prevOfferInclusionsError.current = offerInclusionsError
        }
    }, [offerInclusionsError])


    const handleAddNewInclusion = () => {
        dispatch(clearInclusionForm())
        setCurrentInclusion({ inclusionText: "" })
        setEditingInclusionIndex(null)
        setIsEditMode(false)
        setShowAddForm(true)
        validation.resetForm()
    }

    const handleEditExistingInclusions = () => {
        dispatch(clearInclusionForm())

        offerInclusions.forEach(inclusion => {
            dispatch(addInclusionToForm({
                inclusionText: inclusion
            }))
        })

        setIsEditMode(true)
        setShowAddForm(true)
        setCurrentInclusion({ inclusionText: "" })
        setEditingInclusionIndex(null)
        validation.resetForm()
    }

    const handleCancelForm = () => {
        dispatch(clearInclusionForm())
        setCurrentInclusion({ inclusionText: "" })
        setEditingInclusionIndex(null)
        setIsEditMode(false)
        validation.resetForm()
        if (page !== "add") {
            setShowAddForm(false)
        }
    }


    // Handle input changes
    const handleInputChange = (field, value) => {
        setCurrentInclusion(prev => ({
            ...prev,
            [field]: value
        }))
        validation.setFieldValue(field, value)
    }

    // Create a proper validation object for read-only fields
    const getValidationForReadOnly = () => ({
        errors: {},
        touched: {},
        values: {},
        setFieldValue: () => { },
        handleBlur: () => { },
        isValid: true,
    })

    const getHeaderButton = () => {
        if (page === "add" || showAddForm) {
            return null
        }

        if (!canEdit) {
            return null
        }

        if (offerInclusions.length === 0) {
            return (
                <AccessControlWrapper
                    contextUri={url.MANAGE_OFFER_INCLUSIONS}
                    actionType="post"
                >
                    <CButton.Success
                        icon="bx bx-plus"
                        text="Add Inclusions"
                        onClick={handleAddNewInclusion}
                    />
                </AccessControlWrapper>
            )
        } else {
            return (
                <AccessControlWrapper
                    contextUri={url.MANAGE_OFFER_INCLUSIONS}
                    actionType="post"
                >
                    <CButton.Primary
                        text="Edit Inclusions"
                        onClick={handleEditExistingInclusions}
                    />
                </AccessControlWrapper>
            )
        }
    }

    return (
        <>
            {/* Error Display */}
            {offerInclusionsError && shouldDisplayError(offerInclusionsError) && (
                <Alert color="danger" className="mb-3">
                    {offerInclusionsError.message || "An error occurred"}
                </Alert>
            )}

            {/* Add/Edit Inclusion Form */}
            {showAddForm && (
                <Card className="mb-4">
                    <CardBody>
                        <h6 className="mb-3">
                            {isEditMode ? "Edit Inclusions" : (inclusionFormData.length > 0 ? "Manage Inclusions" : "Add Inclusions")}
                        </h6>

                        <Form
                            className="needs-validation"
                            onSubmit={e => {
                                e.preventDefault()
                                validation.handleSubmit()
                                return false
                            }}
                        >
                            {inclusionFormData.map((inclusion, index) => (
                                <Row key={index} className="align-items-end mb-2">
                                    <Col md="8">
                                        <TextInput
                                            name={`inclusionText_${index}`}
                                            label="Inclusion"
                                            placeholder="Enter inclusion text"
                                            value={editingInclusionIndex === index ? currentInclusion.inclusionText : inclusion.inclusionText}
                                            readOnly={editingInclusionIndex !== index || isSubmitting}
                                            maxLength={500}
                                            required={editingInclusionIndex === index}
                                            onChange={(e) => {
                                                if (editingInclusionIndex === index) {
                                                    handleInputChange('inclusionText', e.target.value)
                                                }
                                            }}
                                            validation={editingInclusionIndex === index ? validation : getValidationForReadOnly()}
                                            inputClassName={editingInclusionIndex !== index ? "text-dark" : ""}
                                        />
                                    </Col>
                                    <Col md="4" className="d-flex align-items-end">
                                        <FormGroup className="d-flex gap-2 mb-2">
                                            {editingInclusionIndex === index ? (
                                                <>
                                                    <IconButton.Save
                                                        onClick={handleAddInclusionToForm}
                                                        title="Update inclusion"
                                                        disabled={isSubmitting || !validation.isValid || !currentInclusion.inclusionText.trim()}
                                                    />
                                                    <IconButton.Cancel
                                                        onClick={handleCancelEdit}
                                                        title="Cancel edit"
                                                        disabled={isSubmitting}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() => handleEditInclusion(inclusion, index)}
                                                        title="Edit inclusion"
                                                        disabled={isSubmitting}
                                                    >
                                                        <i className="bx bx-edit"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleDeleteInclusionFromForm(index)}
                                                        title="Delete inclusion"
                                                        disabled={isSubmitting}
                                                    >
                                                        <i className="bx bx-trash"></i>
                                                    </button>
                                                </>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>
                            ))}

                            {editingInclusionIndex === null && (
                                <Row className="align-items-center mb-3">
                                    <Col md="8">
                                        <TextInput
                                            name="inclusionText"
                                            label="Inclusion"
                                            placeholder="Enter inclusion text"
                                            value={currentInclusion.inclusionText}
                                            readOnly={isSubmitting}
                                            maxLength={500}
                                            required={true}
                                            onChange={(e) => handleInputChange('inclusionText', e.target.value)}
                                            validation={validation}
                                        />
                                    </Col>
                                    <Col md="4" className="d-flex align-items-end">
                                        <FormGroup className="d-flex gap-2 mt-3">
                                            <IconButton.Add
                                                onClick={handleAddInclusionToForm}
                                                disabled={isSubmitting || !validation.isValid || !currentInclusion.inclusionText.trim()}
                                                title="Add inclusion"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            )}
                        </Form>

                        {/* Form Action Buttons */}
                        <div className="text-end">
                            {page === "detail" && (
                                <CButton.Secondary
                                    text="Cancel"
                                    className="me-2"
                                    onClick={handleCancelForm}
                                    disabled={isSubmitting}
                                />
                            )}

                            {(inclusionFormData.length > 0 || isEditMode) && (
                                <ConfirmButton
                                    contextUri={url.MANAGE_OFFER_INCLUSIONS}
                                    actionType="post"
                                    validateBeforeConfirm={() =>
                                        Promise.resolve(isEditMode || inclusionFormData.length > 0)
                                    }
                                    onConfirm={handleFinalSubmit}
                                    message={`Are you sure you want to ${isEditMode ? 'update' : 'save'} these inclusions?`}
                                    disabled={isSubmitting}
                                    renderButton={
                                        <CButton.Primary
                                            text={isEditMode ? "Update Inclusions" : "Save Inclusions"}
                                            loading={isSubmitting}
                                            disabled={isSubmitting}
                                        />
                                    }
                                />
                            )}
                        </div>
                    </CardBody>
                </Card>
            )}

            {/* // Don't show saved listing when copying (data is from source offer, not saved yet) */}
            {!isEditMode && !hasFetchedCopyData.current && (
                <Card>
                    <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Inclusions</h5>
                            {getHeaderButton()}
                        </div>

                        {isLoadingOfferInclusions ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : offerInclusions.length > 0 ? (
                            <ul className="ps-3">
                                {offerInclusions.map((inclusion, index) => (
                                    <li key={index} className="py-1">
                                        {inclusion}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-center py-4">
                                <i className="bx bx-info-circle display-1 text-muted"></i>
                                <p className="text-muted mt-3">No inclusions added yet</p>
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}
        </>
    )
}